package com.corbettcode.allaboutrocks.pages

import androidx.compose.runtime.Composable
import com.corbettcode.allaboutrocks.HeadlineTextStyle
import com.corbettcode.allaboutrocks.HeroContainerStyle
import com.corbettcode.allaboutrocks.PageHeadlineStyle
import com.corbettcode.allaboutrocks.PageListStyle
import com.corbettcode.allaboutrocks.PageSectionTitleStyle
import com.corbettcode.allaboutrocks.SubheadlineTextStyle
import com.corbettcode.allaboutrocks.components.layouts.PageLayoutData
import com.corbettcode.allaboutrocks.components.widgets.RockTypeCarousel
import com.corbettcode.allaboutrocks.toSitePalette
import com.corbettcode.allaboutrocks.utility.textColor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment.CenterHorizontally
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.ColorPalettes
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

private val IMAGE_MAX_WIDTH = 300.px

@InitRoute
fun initHomePage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Home"))
}

/** One signposted destination elsewhere on the site. */
private data class SiteLink(val label: String, val route: String, val blurb: String)

/** The rock pages, cycle and families first, then the individual varieties. */
private val rockTypeLinks = listOf(
    //SiteLink("The Rock Cycle", "/types_of_rocks/the_rock_cycle", "How igneous, sedimentary, and metamorphic rock turn into one another.")
    //, SiteLink("Igneous", "/types_of_rocks/igneous", "Cooled from magma or lava — basalt, granite, obsidian.")
    //, SiteLink("Sedimentary", "/types_of_rocks/sedimentary", "Layered, often fossil-bearing — sandstone, shale, limestone.")
    //, SiteLink("Metamorphic", "/types_of_rocks/metamorphic", "Reworked by heat and pressure — gneiss, marble, slate.")
    SiteLink("Agate", "/types_of_rocks/agate", "Banded chalcedony, and the classic tumbling stone.")
    , SiteLink("Carnelian", "/types_of_rocks/carnelian", "Warm orange to deep red chalcedony.")
    , SiteLink("Chalcedony", "/types_of_rocks/chalcedony", "The microcrystalline quartz family the rest belong to.")
    , SiteLink("Epidote", "/types_of_rocks/epidote", "Pistachio-green crystals, often threaded through quartz.")
    , SiteLink("Jasper", "/types_of_rocks/jasper", "Opaque and richly patterned, no two pieces alike.")
    // Route is camel-cased unlike its neighbours; linked here exactly as the page declares it.
    , SiteLink("Petrified Wood", "/types_of_rocks/PetrifiedWood", "Wood turned to stone with the grain still readable.")
    , SiteLink("Quartz", "/types_of_rocks/quartz", "The most common mineral you will pick up, in every color.")
)

/** The how-to pages. */
private val learnLinks = listOf(
    SiteLink("Overview", "/learn_identify_rocks", "Where to start: working out what you have actually found.")
    , SiteLink("Tumble Rocks", "/learn_tumble_rocks", "Turning rough finds into polished stones.")
    , SiteLink("Books", "/learn_books", "Field guides and references worth owning.")
)

private val placeLinks = listOf(
    SiteLink("Awesome Places", "/awesome-places", "Twenty collecting sites worth the drive, grouped by region.")
)

private val resourceLinks = listOf(
    SiteLink("Resources", "/resources", "Clubs, groups, channels, and reference sites worth keeping to hand.")
)

@Composable
private fun HomeSectionTitle(text: String) {
    SpanText(
        text
        , PageSectionTitleStyle.toModifier()
            .color(textColor())
    )
}

@Composable
private fun HomeLinkList(items: List<SiteLink>) {
    Ul(PageListStyle.toAttrs()) {
        items.forEach { (label, route, blurb) ->
            Li {
                Link(route, label)
                Text(" — $blurb")
            }
        }
    }
}

@Composable
private fun HomeHeadline() {
    val sitePalette = ColorMode.current.toSitePalette()

    Div(HeadlineTextStyle.toAttrs()) {
        SimpleGrid(
            numColumns = numColumns(
                1
                , md = 2
            )
            , modifier = HeroContainerStyle
                .toModifier()
                .fillMaxWidth()
                .gap(2.cssRem)
        ) {
            Column {
                SpanText(
                    "Welcome to"
                    , PageHeadlineStyle.toModifier()
                        .color(textColor())
                        .align(alignment = CenterHorizontally)
                )
                SpanText(
                    "All About Rocks"
                    , PageHeadlineStyle.toModifier()
                        .color(sitePalette.brand.accent)
                        // Use a shadow so this light-colored word is more visible in light mode
                        .textShadow(
                            0.px
                            , 0.px
                            , blurRadius = 0.5.cssRem
                            , color = Colors.Gray
                        )
                        .align(alignment = CenterHorizontally)
                )
            }

            Image(
                "/images/agate.png"
                , "image of agate"
                , Modifier
                    .maxWidth(IMAGE_MAX_WIDTH)
                    .fillMaxWidth()  // shrinks naturally on small screens
            )
        }
    }
}

@Composable
private fun HomeHero() {

    HomeHeadline()

    // 1 column on mobile, 2 columns on md+ (carousel left, welcome text right)
    SimpleGrid(
        numColumns = numColumns(1, md = 2)
        , modifier = HeroContainerStyle
            .toModifier()
            .fillMaxWidth()
            .gap(2.cssRem)
    ) {
        Box {
            RockTypeCarousel(
                Modifier
                    .fillMaxWidth()
                    // Below this width the carousel is too cramped to read
                    .minWidth(280.px)
                    .maxWidth(400.px)
            )
        }

        Column(Modifier.gap(2.cssRem)) {
            P {
                Text("Rocks are the oldest things you can hold. Pick one up and you are holding a record of where it formed, what happened to it since, and how it came to be lying at your feet. This site is for anyone who wants to read that record — what the three rock families are, how to tell them apart in the field, how to polish what you find, and where to go looking.")
            }

            Div(SubheadlineTextStyle.toAttrs()) {
                SpanText("You can read the ")
                Link("/about", "About")
                SpanText(" page for more information.")
            }
        }
    }

    HorizontalDivider(Modifier
        .fillMaxWidth()
        .margin(topBottom = 2.cssRem)
    )

    Column(
        Modifier
            .fillMaxWidth()
            .gap(2.cssRem)
    ) {
        HomeSectionTitle("Types of Rocks")
        P {
            Text("Every rock belongs to one of three families, and the varieties below are the ones you are most likely to come across:")
        }
        HomeLinkList(rockTypeLinks)

        HomeSectionTitle("Learn")
        P {
            Text("Field skills, from identifying a mystery pebble to putting a shine on it:")
        }
        HomeLinkList(learnLinks)

        HomeSectionTitle("Awesome Places")
        P {
            Text("Rockhounding is only half about the rocks; the rest is knowing where to look:")
        }
        HomeLinkList(placeLinks)

        HomeSectionTitle("Resources")
        P {
            Text("Nobody learns this alone. Clubs, groups, and the references worth bookmarking:")
        }
        HomeLinkList(resourceLinks)
    }
}

@Page
@Layout(".components.layouts.PageLayout")
@Composable
fun HomePage() {
    HomeHero()
}
